import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	getDisplayedExposures,
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import {
	Exposure,
	ExposureCreateRequest,
	Peril,
	PerilCreateRequest,
} from '../../../types/policy';
import { useRouteMatch } from '../../../util/useRouteMatch';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import TreeNode, {
	TreeData,
	TreeItemData,
	TreeItemInterface,
} from '../../layouts/SidePanel/TreeNode';
import {
	validateAddExposureEndorsement,
	validatePeril,
	validatePolicyDetails,
	validateUpdateExposureEndorsement,
} from '../fields/validations';
import {
	getExposureDisplayNameAndUrl,
	getExposuresForRenewal,
	getPerilDisplayNameAndUrl,
} from '../Renewals/util';
import { getUpdatedExposureObj } from '../util_sharedEndorsementAndRenewal';

interface Props {
	renewalData: RenewalData;
	errorData: ErrorData;
}

export interface RenewalRoutes {
	ROOT: string;
	POLICY_DETAILS: string;
	EXPOSURES: string;
	PRICE_BREAKDOWN: string;
	REVIEW_CHANGES: string;
	INVOICES: string;
	AUX_DATA: string;
	DOCUMENTS: string;
	INVOICE: string;
	EXPOSURE: string;
	PERILS: string;
	PERIL: string;
}

export const ROUTES: RenewalRoutes = {
	ROOT: '/policy/:policyId/renewals/:endorsementId',
	POLICY_DETAILS: '/policy/:policyId/renewals/:endorsementId/policyDetails',
	EXPOSURES: '/policy/:policyId/renewals/:endorsementId/exposures',
	PRICE_BREAKDOWN: '/policy/:policyId/renewals/:endorsementId/price-breakdown',
	REVIEW_CHANGES: '/policy/:policyId/renewals/:endorsementId/review',
	INVOICES: '/policy/:policyId/renewals/:endorsementId/invoices',
	AUX_DATA: '/policy/:policyId/renewals/:endorsementId/auxData',
	DOCUMENTS: '/policy/:policyId/renewals/:endorsementId/documents',
	INVOICE: '/policy/:policyId/renewals/:endorsementId/invoices/:invoiceId',
	EXPOSURE: '/policy/:policyId/renewals/:endorsementId/exposures/:exposureId',
	PERILS:
		'/policy/:policyId/renewals/:endorsementId/exposures/:exposureId/perils',
	PERIL:
		'/policy/:policyId/renewals/:endorsementId/exposures/:exposureId/perils/:perilId',
};

const RenewalSidebar = (props: Props) => {
	const { t } = useTranslation();
	const { renewalData, errorData } = props;
	const authData = useAuthContext();
	const { currentErrorObject } = errorData;

	const { policy, productConfig, renewalUpdateObj, selectedExposure } =
		renewalData;

	const { exposures } = policy;
	const routeMatch = useRouteMatch(
		Object.keys(ROUTES).map((key) => ROUTES[key as keyof RenewalRoutes]),
	);
	const params = routeMatch?.params || {};
	const routeName = routeMatch?.pattern?.path || '';

	if (!policy) {
		return <LargeCenterSpinner />;
	}

	const getInitialExpandedItems = () => {
		const ids = ['Exposures'] as string[];
		// if looking at a certain exposure, expand that exposure and it's perils
		if (selectedExposure) {
			const selectedExposureLocator =
				(selectedExposure as Exposure).locator ||
				(selectedExposure as ExposureCreateRequest).exposureLocator;
			ids.push(selectedExposureLocator);
			ids.push(`${selectedExposureLocator}-Perils`);
		}
		return ids;
	};

	const [itemsExpanded, setItemsExpanded] = useState([]);

	useEffect(() => {
		const initialIds = getInitialExpandedItems();
		setItemsExpanded(initialIds);
	}, [params.invoiceId, renewalData.selectedExposure]);

	const renewalEffectiveDate = renewalUpdateObj.renewalStartTimestamp;
	const currentExposures = getExposuresForRenewal(
		renewalEffectiveDate,
		policy,
		exposures,
		[],
		[],
	);
	const displayedExposures = getDisplayedExposures(
		currentExposures,
		renewalUpdateObj,
	);
	const policyConfig = productConfig.policyConfiguration;

	const renewalLink = `/policy/${policy.locator}/renewals/${renewalUpdateObj.locator}`;
	const policyDetailsLink = `${renewalLink}/policyDetails`;
	const reviewChangesLink = `${renewalLink}/review`;
	const exposuresLink = `${renewalLink}/exposures`;
	const priceBreakdownLink = `${renewalLink}/price-breakdown`;
	const invoicesLink = `${renewalLink}/invoices`;
	const documentsLink = `${renewalLink}/documents`;
	const policyDetailErrors =
		!!currentErrorObject?.data?.policyFieldErrors?.policyFieldErrors?.hasErrors;
	const exposureErrors =
		!!currentErrorObject?.data?.policyFieldErrors?.exposureValidationErrors
			?.length;

	const exposureListItem: TreeItemInterface = {
		id: 'Exposures',
		children: [],
		hasChildren: !!displayedExposures.length,
		isExpanded: itemsExpanded.includes('Exposures'),
		isChildrenLoading: false,
		data: {
			title: `${t('exposure.plural.upper')}`,
			link: exposuresLink,
			selected: routeName === ROUTES.EXPOSURES,
			hasError: exposureErrors,
		},
	};

	// Exposures drawer
	const exposureAndPerilItems = displayedExposures.reduce(
		(
			exposureItems: TreeItemData,
			exposure: Exposure | ExposureCreateRequest,
		) => {
			const {
				urlId: exposureUrlId,
				exposureDisplayName,
				exposureConfig,
			} = getExposureDisplayNameAndUrl(
				exposure,
				renewalUpdateObj,
				productConfig,
			);
			const isExposureAdded = !(exposure as Exposure).displayId;
			const perilsLink = `${exposuresLink}/${exposureUrlId}/perils`;

			// Actually a Peril[] | PerilCreateRquest[] but using Peril[] since we only need the name and locator
			// keys and they exist on both types. Otherwise filter and reduce function will complain about type
			// error
			let perils = (exposure.perils || []) as Peril[];

			if (!isExposureAdded) {
				// If the exposure is added then we need to concatenate any added perils to the existing perils.
				const exposureUpdateRequest = getUpdatedExposureObj(
					renewalUpdateObj,
					(exposure as Exposure).locator,
				);
				const addPerils = exposureUpdateRequest.addPerils || [];
				const endPerils = exposureUpdateRequest.endPerils || [];
				// updatedExposures and endPerils are relevant for existing exposures only.
				perils = ([...perils, ...addPerils] as Peril[]).filter((peril) => {
					return !endPerils.find(
						(endPerilLocator) => endPerilLocator === peril.locator,
					);
				});
			}

			// Perils drawer
			const perilTreeItems = perils.reduce(
				(perilItems: TreeItemData, peril: Peril | PerilCreateRequest) => {
					const {
						urlId: perilUrlId,
						perilDisplayName,
						perilConfig,
					} = getPerilDisplayNameAndUrl(
						exposure,
						peril,
						renewalUpdateObj,
						exposureConfig,
						policy,
					);

					const perilLink = `${perilsLink}/${perilUrlId}`;
					const perilId = peril.locator;
					const isPerilPage = !!params.perilId;
					const isPerilIdSelected =
						isPerilPage && perilUrlId === params.perilId;
					const isPerilAdded = !(peril as Peril).displayId;
					const isPerilValid =
						isExposureAdded || isPerilAdded
							? true
							: validatePeril(
									peril as Peril,
									exposure as Exposure,
									perilConfig,
									renewalEffectiveDate,
							  );

					perilItems[perilId] = {
						id: perilId,
						children: [],
						hasChildren: false,
						isExpanded: itemsExpanded.includes(perilId),
						isChildrenLoading: false,
						data: {
							title: perilDisplayName,
							link: perilLink,
							className: 'peril',
							selected: isPerilIdSelected,
							hasError: !isPerilValid,
						},
					};

					return perilItems;
				},
				{} as TreeItemData,
			);

			const perilTreeKeys = Object.keys(perilTreeItems);
			// Exposure locator is used for tree node id instead of urlId since urlId can have duplicates (i.e. added-0)
			const exposureLocator =
				(exposure as Exposure).locator ||
				(exposure as ExposureCreateRequest).exposureLocator;
			const exposureLink = `${exposuresLink}/${exposureUrlId}`;
			const isExposurePage =
				!!params.exposureId && !params.perilId && routeName !== ROUTES.PERILS;
			// let isExposureIdSelected = isExposurePage && exposureLocator === params.exposureId;
			const isExposureIdSelected =
				isExposurePage && exposureUrlId === params.exposureId;
			let isExposureValid = true;

			if (isExposureAdded) {
				isExposureValid = validateAddExposureEndorsement(
					exposure as ExposureCreateRequest,
					exposureConfig,
				).valid;
			} else {
				const existingExposure = exposures.find(
					(e) => e.locator === exposureLocator,
				);
				const updateExposureObj = renewalUpdateObj.updateExposures?.find(
					(e) => e.exposureLocator === existingExposure.locator,
				);
				const timestamp = renewalUpdateObj.renewalStartTimestamp;
				isExposureValid = updateExposureObj
					? validateUpdateExposureEndorsement(
							existingExposure,
							updateExposureObj,
							exposureConfig,
							timestamp,
					  ).valid
					: true;
			}

			const exposureItem: TreeItemInterface = {
				id: exposureLocator,
				children: [],
				hasChildren: true,
				isExpanded: itemsExpanded.includes(exposureLocator),
				isChildrenLoading: false,
				data: {
					title: exposureDisplayName,
					link: exposureLink,
					className: 'exposure',
					selected: isExposureIdSelected,
					hasError: !isExposureValid,
				},
			};

			const perilItemId = `${exposureLocator}-Perils`;
			const addedPerilItemId = `${exposureLocator}-Perils-Added`;
			const perilItem: TreeItemInterface = {
				id: perilItemId,
				children: perilTreeKeys,
				hasChildren: !!perils.length,
				isExpanded: itemsExpanded.includes(perilItemId),
				isChildrenLoading: false,
				data: {
					title: `${t('peril.plural.upper')}`,
					link: perilsLink,
					className: 'perils',
					selected:
						exposureUrlId === params.exposureId && routeName === ROUTES.PERILS,
				},
			};

			perilTreeItems[perilItemId] = perilItem;
			exposureItem.children.push(perilItemId);
			exposureItem.children.push(addedPerilItemId);

			exposureItems[exposureLocator] = exposureItem;
			exposureListItem.children.push(exposureLocator);

			return { ...exposureItems, ...perilTreeItems };
		},
		{},
	);

	const getRootItem = () => {
		const children = [];

		if (renewalUpdateObj.state === 'application') {
			children.push('Policy Details');
			children.push('Exposures');
		}

		children.push('Price Breakdown');
		children.push('Review Changes');

		if (renewalUpdateObj.state !== 'application') {
			if (authData.hasPermission('invoice.fetch')) {
				children.push('Invoices');
			}

			children.push('Documents');
		}

		return {
			id: 'root',
			children,
			hasChildren: true,
			isExpanded: true,
			isChildrenLoading: false,
			data: {
				title: 'root',
			},
		} as TreeItemInterface;
	};

	const getItems = () => {
		exposureListItem.hasChildren = !!exposureListItem.children.length;
		const isPolicyValid = validatePolicyDetails(
			policy,
			policyConfig,
			`${Date.now()}`,
		).valid;
		const isDetailsSelected =
			!!params.policyId &&
			!params.exposureId &&
			(routeName === ROUTES.POLICY_DETAILS || routeName === ROUTES.AUX_DATA);

		const items: TreeItemData = {
			root: getRootItem(),
			'Policy Details': {
				id: 'Policy Details',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Policy Details',
					link: policyDetailsLink,
					selected: isDetailsSelected,
					hasError: policyDetailErrors || !isPolicyValid,
				},
			},
			Exposures: exposureListItem,
			...exposureAndPerilItems,
			'Review Changes': {
				id: 'Review Changes',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Review Changes',
					link: reviewChangesLink,
					selected: routeName === ROUTES.REVIEW_CHANGES,
				},
			},
			'Price Breakdown': {
				id: 'Price Breakdown',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Price Breakdown',
					link: priceBreakdownLink,
					selected: routeName === ROUTES.PRICE_BREAKDOWN,
				},
			},
			Invoices: {
				id: 'Invoices',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Invoices',
					link: invoicesLink,
					selected:
						routeName === ROUTES.INVOICES || routeName === ROUTES.INVOICE,
				},
			},
			Documents: {
				id: 'Documents',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Documents',
					link: documentsLink,
					selected: routeName === ROUTES.DOCUMENTS,
				},
			},
		};

		return {
			...items,
		};
	};

	const initializeTree = () => {
		const sidePanelData: TreeData = {
			rootId: 'root',
			items: getItems(),
		};

		return sidePanelData;
	};

	const tree = initializeTree();

	return <TreeNode defaultExpanded={itemsExpanded} treeData={tree} />;
};

export default withRenewalContext<Props>(RenewalSidebar);
